import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'

// ADR-025 (9.2): one button family for auth/settings — capsule, 700, 44pt
// minimum, a 100ms press state (transform only; the house reduce-motion
// pattern keeps the instant scale and drops the transition).
const buttonVariants = cva(
	'ring-ring ring-offset-background inline-flex items-center justify-center rounded-full font-bold ring-offset-2 outline-hidden transition-[color,background-color,transform] duration-100 focus-within:ring-2 focus-visible:ring-2 active:scale-[.97] disabled:pointer-events-none disabled:opacity-55 motion-reduce:transition-none',
	{
		variants: {
			variant: {
				// hover darkens in light and lightens in dark (4.98 / 7.15:1)
				default:
					'bg-primary text-primary-foreground hover:bg-[color-mix(in_oklab,var(--primary)_92%,var(--foreground))]',
				// HIG keeps red for destructive; not used at rest in /settings (9.5)
				destructive:
					'bg-destructive text-destructive-foreground hover:bg-destructive/80',
				outline:
					'border-border bg-card text-foreground hover:bg-accent border-2',
				secondary:
					'bg-secondary text-secondary-foreground hover:bg-secondary/80',
				// = .pe-actbtn.stil
				ghost:
					'text-foreground hover:bg-accent bg-[color-mix(in_oklab,var(--card)_55%,var(--background))]',
				link: 'text-primary underline-offset-4 hover:underline',
			},
			size: {
				default: 'min-h-11 px-4 text-sm',
				sm: 'min-h-11 px-3 text-[0.84rem]',
				lg: 'min-h-11 px-6 text-base',
				// = .pe-primary (52px, full width, 2xl radius, 800)
				block: 'min-h-13 w-full rounded-2xl px-4 text-base font-extrabold',
				wide: 'min-h-13 w-full rounded-2xl px-4 text-base font-extrabold',
				pill: 'min-h-11 px-12 text-sm',
				icon: 'size-11',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
)

export type ButtonVariant = VariantProps<typeof buttonVariants>

const Button = ({
	className,
	variant,
	size,
	asChild = false,
	...props
}: React.ComponentProps<'button'> &
	ButtonVariant & {
		asChild?: boolean
	}) => {
	const Comp = asChild ? Slot : 'button'
	return (
		<Comp
			data-slot="button"
			className={cn(buttonVariants({ variant, size, className }))}
			{...props}
		/>
	)
}

export { Button, buttonVariants }
